import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ActiveTest } from '../utils/local-api';
import { api, UserExamRestrictions } from '../utils/api';
import {
  BookOpen,
  Shuffle,
  FileText,
  PenTool,
  PlayCircle,
  Lock,
  ChevronRight,
} from 'lucide-react';
import HSK30Systems, { CSCASystems } from '../components/HSK30Systems';

interface DashboardDesktopProps {
  activeTests: ActiveTest[];
  historyCount: Record<string, number>;
  restrictions: UserExamRestrictions;
}

type ExamVersion = '2.0' | '3.0' | 'CSCA';

const DashboardDesktop: React.FC<DashboardDesktopProps> = ({ activeTests, historyCount, restrictions }) => {
  const navigate = useNavigate();

  const availableVersions: Array<{ id: ExamVersion; label: string }> = [
    ...(!restrictions.hsk2Restricted && restrictions.hsk2Levels.length > 0 ? [{ id: '2.0' as const, label: 'HSK 2.0' }] : []),
    ...(!restrictions.hsk3Restricted && restrictions.hsk3Levels.length > 0 ? [{ id: '3.0' as const, label: 'HSK 3.0' }] : []),
    ...(!restrictions.cscaRestricted && restrictions.cscaLevels.length > 0 ? [{ id: 'CSCA' as const, label: 'CSCA' }] : []),
  ];

  const [selectedVersion, setSelectedVersion] = useState<ExamVersion>(availableVersions[0]?.id || '2.0');
  const [selectedLevel, setSelectedLevel] = useState<number | null>(() => {
    const first = availableVersions[0]?.id;
    return first === '3.0' ? restrictions.hsk3Levels[0] || null : first === 'CSCA' ? restrictions.cscaLevels[0] || null : restrictions.hsk2Levels[0] || null;
  });

  useEffect(() => {
    if (availableVersions.some(version => version.id === selectedVersion)) return;
    const first = availableVersions[0]?.id;
    if (!first) return;
    setSelectedVersion(first);
    setSelectedLevel(first === '3.0' ? restrictions.hsk3Levels[0] || null : first === 'CSCA' ? restrictions.cscaLevels[0] || null : restrictions.hsk2Levels[0] || null);
  }, [availableVersions, restrictions, selectedVersion]);

  const [showRandomTestModal, setShowRandomTestModal] = useState(false);
  const [randomTestConfig, setRandomTestConfig] = useState({
    levels: [3],
    parts: ['listening', 'reading'],
    numQuestions: 20,
  });

  const handleRandomTest = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const data = await api.generateRandomTest(randomTestConfig, token);
      navigate('/test/random', { state: { testData: data } });
    } catch (error) {
      console.error('Error generating random test:', error);
    }
    setShowRandomTestModal(false);
  };

  const renderLevelButton = (level: number, version: '2.0' | '3.0') => {
    const isActive = selectedVersion === version && selectedLevel === level;
    const isAvailable = version === '3.0' || (version === '2.0' && [2, 3, 4].includes(level));

    return (
      <button
        key={`${version}-${level}`}
        onClick={() => {
          if (isAvailable) setSelectedLevel(level);
        }}
        className={`
            relative p-4 rounded-2xl border-2 transition-all duration-200 flex flex-col items-center justify-center gap-2 h-32
            ${isActive
              ? 'border-[#4F46E5] bg-indigo-50 shadow-md scale-105 z-10'
              : 'border-gray-100 bg-white hover:border-indigo-100 hover:bg-gray-50'
            }
            ${!isAvailable ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        <div className={`
            w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl
            ${isActive ? 'bg-[#4F46E5] text-white' : 'bg-gray-100 text-gray-400'}
        `}>
          {level}
        </div>
        <span className={`font-bold ${isActive ? 'text-[#312E81]' : 'text-gray-400'}`}>HSK {level}</span>

        {!isAvailable && (
          <div className="absolute top-2 right-2 text-gray-300">
            <Lock className="w-4 h-4" />
          </div>
        )}
      </button>
    );
  };

  const renderActiveTests = () => (
    activeTests.length > 0 && (
      <div className="mt-8">
        <h2 className="text-xl font-bold text-[#312E81] mb-4">Continue Learning</h2>
        <div className="grid gap-4">
          {activeTests.map(test => {
            const key = `${test.bank_version || '2.0'}-${test.system_type || 'simulation'}-${test.level}-${test.part}-${test.workbook_part || ''}-${test.paper_id || ''}-${test.topic_coarse || ''}-${test.topic_fine || ''}`;
            const num = (historyCount[key] || 0) + 1;
            const percent = Math.round(((test.current_index + 1) / test.total) * 100);
            const resumePath = test.system_type === 'random_paper'
              ? '/csca/random/exam'
              : test.system_type === 'mooc_random'
              ? '/csca/mooc/random/exam'
              : test.system_type === 'mooc_topic_practice' && test.topic_coarse
              ? `/csca/mooc/topic-practice?${new URLSearchParams({ coarse: test.topic_coarse, ...(test.topic_fine ? { fine: test.topic_fine } : {}), count: String(test.total) }).toString()}`
              : test.system_type === 'topic_practice' && test.topic_coarse
              ? `/csca/topic-practice?${new URLSearchParams({ coarse: test.topic_coarse, ...(test.topic_fine ? { fine: test.topic_fine } : {}), count: String(test.total) }).toString()}`
              : test.system_type === 'past_paper' && test.paper_id
              ? `/csca/past-papers/${test.paper_id}`
              : test.system_type === 'workbook'
              ? `/hsk3/workbook/hsk${test.level}`
              : test.bank_version === '3.0'
                ? `/hsk3/simulation/hsk${test.level}`
                : test.part === 'simulation' ? `/simulation/hsk${test.level}` : `/test/hsk${test.level}/${test.part}`;

            return (
              <div key={test.id} className="bg-white border border-indigo-50 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-indigo-50 rounded-xl">
                    <PlayCircle className="w-6 h-6 text-[#4F46E5]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#312E81]">
                      {test.system_type === 'mooc_random' ? 'CSCA 数学 · 随机 MOOC' : test.system_type === 'mooc_topic_practice' ? `CSCA 数学 · MOOC · ${test.topic_fine ? `${test.topic_coarse} / ${test.topic_fine}` : test.topic_coarse} · 分类练习` : test.system_type === 'random_paper' ? 'CSCA 数学 · 随机组卷' : test.system_type === 'topic_practice' ? `CSCA 数学 · ${test.topic_fine ? `${test.topic_coarse} / ${test.topic_fine}` : test.topic_coarse} · 题型练习` : test.system_type === 'past_paper' ? `CSCA 数学 ${test.paper_year || ''}年${test.paper_month || ''}月` : `HSK ${test.level} ${test.system_type === 'workbook' ? `Workbook${test.workbook_part ? ` · ${test.workbook_part}` : ''}` : test.part.charAt(0).toUpperCase() + test.part.slice(1)}`} ({num})
                    </h3>
                    <div className="flex items-center gap-3 mt-1">
                      <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="bg-[#4F46E5] h-full rounded-full" style={{ width: `${percent}%` }}></div>
                      </div>
                      <span className="text-xs text-gray-400">{percent}% Complete</span>
                    </div>
                  </div>
                </div>
                <Link
                  to={test.part === 'simulation' && (test.bank_version || '2.0') === '2.0' ? `${resumePath}?testId=${encodeURIComponent(test.id)}` : resumePath}
                  className="px-4 py-2 bg-[#4F46E5] text-white rounded-xl font-bold text-sm hover:bg-indigo-700 transition-colors"
                >
                  Resume
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    )
  );

  return (
    <div className="space-y-8 max-w-5xl mx-auto">
      <div className="text-center space-y-2">
        <h1 className="text-3xl md:text-4xl font-bold text-[#312E81]">Dashboard</h1>
        <p className="text-[#475569]">Select an exam system and level or subject to start practicing.</p>
      </div>

      <div className="flex justify-center">
        <div className="bg-white p-1 rounded-2xl shadow-sm border border-gray-100 inline-flex">
          {availableVersions.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => {
                setSelectedVersion(id);
                if (id === '3.0') setSelectedLevel(restrictions.hsk3Levels[0] || null);
                if (id === '2.0') setSelectedLevel(restrictions.hsk2Levels[0] || null);
                if (id === 'CSCA') setSelectedLevel(restrictions.cscaLevels[0] || null);
              }}
              className={`
                px-8 py-3 rounded-xl font-bold text-lg transition-all
                ${selectedVersion === id
                  ? 'bg-[#4F46E5] text-white shadow-md'
                  : 'text-gray-400 hover:text-[#312E81]'
                }
              `}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {selectedVersion === '2.0' && (
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-4">
          {restrictions.hsk2Levels.map(l => renderLevelButton(l, selectedVersion))}
        </div>
      )}

      {availableVersions.length === 0 ? (
        <div className="rounded-3xl border border-gray-100 bg-white p-10 text-center text-gray-500">当前账户没有已开放的考试体系。</div>
      ) : selectedVersion === '3.0' ? (
        <HSK30Systems allowedLevels={restrictions.hsk3Levels} />
      ) : selectedVersion === 'CSCA' ? (
        <CSCASystems allowedLevels={restrictions.cscaLevels} />
      ) : selectedLevel === 3 ? (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="grid grid-cols-2 gap-6">
            <Link to="/simulation/hsk3" className="group bg-gradient-to-br from-orange-500 to-red-500 rounded-3xl p-6 shadow-lg shadow-orange-500/20 text-white transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-500/30">
              <div className="flex justify-between items-start mb-4">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-white backdrop-blur-sm group-hover:scale-110 transition-transform">
                  <Shuffle className="w-7 h-7" />
                </div>
                <div className="bg-white/20 text-white px-3 py-1 rounded-full text-xs font-bold backdrop-blur-sm">Full Exam</div>
              </div>
              <h3 className="text-xl font-bold mb-2">Full Simulation</h3>
              <p className="text-orange-100 text-sm mb-4">Listening + Reading + Writing</p>
              <div className="flex items-center text-white font-bold text-sm">
                Start Exam <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link to="/test/hsk3/listening" className="group bg-white rounded-3xl p-6 border border-indigo-50 shadow-sm hover:shadow-xl hover:shadow-indigo-500/10 transition-all hover:-translate-y-1">
              <div className="flex justify-between items-start mb-4">
                <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center text-[#4F46E5] group-hover:scale-110 transition-transform">
                  <BookOpen className="w-7 h-7" />
                </div>
                <div className="bg-indigo-50 text-[#4F46E5] px-3 py-1 rounded-full text-xs font-bold">Part 1</div>
              </div>
              <h3 className="text-xl font-bold text-[#312E81] mb-2">Listening Test</h3>
              <p className="text-gray-500 text-sm mb-4">40 Questions • 35 Minutes</p>
              <div className="flex items-center text-[#4F46E5] font-bold text-sm">
                Start Practice <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link to="/test/hsk3/reading" className="group bg-white rounded-3xl p-6 border border-emerald-50 shadow-sm hover:shadow-xl hover:shadow-emerald-500/10 transition-all hover:-translate-y-1">
              <div className="flex justify-between items-start mb-4">
                <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center text-emerald-600 group-hover:scale-110 transition-transform">
                  <FileText className="w-7 h-7" />
                </div>
                <div className="bg-emerald-50 text-emerald-600 px-3 py-1 rounded-full text-xs font-bold">Part 2</div>
              </div>
              <h3 className="text-xl font-bold text-[#312E81] mb-2">Reading Test</h3>
              <p className="text-gray-500 text-sm mb-4">30 Questions • 30 Minutes</p>
              <div className="flex items-center text-emerald-600 font-bold text-sm">
                Start Practice <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link to="/test/hsk3/writing" className="group bg-white rounded-3xl p-6 border border-purple-50 shadow-sm hover:shadow-xl hover:shadow-purple-500/10 transition-all hover:-translate-y-1">
              <div className="flex justify-between items-start mb-4">
                <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center text-purple-600 group-hover:scale-110 transition-transform">
                  <PenTool className="w-7 h-7" />
                </div>
                <div className="bg-purple-50 text-purple-600 px-3 py-1 rounded-full text-xs font-bold">Part 3</div>
              </div>
              <h3 className="text-xl font-bold text-[#312E81] mb-2">Writing Test</h3>
              <p className="text-gray-500 text-sm mb-4">10 Items • 15 Minutes</p>
              <div className="flex items-center text-purple-600 font-bold text-sm">
                Start Practice <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>

          {renderActiveTests()}
        </div>
      ) : selectedLevel === 4 ? (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="grid grid-cols-2 gap-6">
            <Link to="/simulation/hsk4" className="group bg-gradient-to-br from-orange-500 to-red-500 rounded-3xl p-6 shadow-lg shadow-orange-500/20 text-white transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-500/30">
              <div className="flex justify-between items-start mb-4">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-white backdrop-blur-sm group-hover:scale-110 transition-transform">
                  <Shuffle className="w-7 h-7" />
                </div>
                <div className="bg-white/20 text-white px-3 py-1 rounded-full text-xs font-bold backdrop-blur-sm">HSK 4</div>
              </div>
              <h3 className="text-xl font-bold mb-2">Full Simulation</h3>
              <p className="text-orange-100 text-sm mb-4">100 Questions • Listening + Reading + Writing</p>
              <div className="flex items-center text-white font-bold text-sm">
                Start Exam <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link to="/test/hsk4/listening" className="group bg-white rounded-3xl p-6 border border-indigo-50 shadow-sm hover:shadow-xl hover:shadow-indigo-500/10 transition-all hover:-translate-y-1">
              <div className="flex justify-between items-start mb-4">
                <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center text-[#4F46E5] group-hover:scale-110 transition-transform">
                  <BookOpen className="w-7 h-7" />
                </div>
                <div className="bg-indigo-50 text-[#4F46E5] px-3 py-1 rounded-full text-xs font-bold">Part 1</div>
              </div>
              <h3 className="text-xl font-bold text-[#312E81] mb-2">Listening Test</h3>
              <p className="text-gray-500 text-sm mb-4">45 题 • 可自定义题量</p>
              <div className="flex items-center text-[#4F46E5] font-bold text-sm">
                Start Practice <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link to="/test/hsk4/reading" className="group bg-white rounded-3xl p-6 border border-emerald-50 shadow-sm hover:shadow-xl hover:shadow-emerald-500/10 transition-all hover:-translate-y-1">
              <div className="flex justify-between items-start mb-4">
                <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center text-emerald-600 group-hover:scale-110 transition-transform">
                  <FileText className="w-7 h-7" />
                </div>
                <div className="bg-emerald-50 text-emerald-600 px-3 py-1 rounded-full text-xs font-bold">Part 2</div>
              </div>
              <h3 className="text-xl font-bold text-[#312E81] mb-2">Reading Test</h3>
              <p className="text-gray-500 text-sm mb-4">40 题 • 可自定义题量</p>
              <div className="flex items-center text-emerald-600 font-bold text-sm">
                Start Practice <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link to="/test/hsk4/writing" className="group bg-white rounded-3xl p-6 border border-purple-50 shadow-sm hover:shadow-xl hover:shadow-purple-500/10 transition-all hover:-translate-y-1">
              <div className="flex justify-between items-start mb-4">
                <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center text-purple-600 group-hover:scale-110 transition-transform">
                  <PenTool className="w-7 h-7" />
                </div>
                <div className="bg-purple-50 text-purple-600 px-3 py-1 rounded-full text-xs font-bold">Part 3</div>
              </div>
              <h3 className="text-xl font-bold text-[#312E81] mb-2">Writing Test</h3>
              <p className="text-gray-500 text-sm mb-4">15 题 • 可自定义题量</p>
              <div className="flex items-center text-purple-600 font-bold text-sm">
                Start Practice <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>

          {renderActiveTests()}
        </div>
      ) : selectedLevel === 2 ? (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="grid grid-cols-2 gap-6">
            <Link to="/simulation/hsk2" className="group bg-gradient-to-br from-orange-500 to-red-500 rounded-3xl p-6 shadow-lg shadow-orange-500/20 text-white transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-500/30">
              <div className="flex justify-between items-start mb-4">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-white backdrop-blur-sm group-hover:scale-110 transition-transform">
                  <Shuffle className="w-7 h-7" />
                </div>
                <div className="bg-white/20 text-white px-3 py-1 rounded-full text-xs font-bold backdrop-blur-sm">HSK 2</div>
              </div>
              <h3 className="text-xl font-bold mb-2">Full Simulation</h3>
              <p className="text-orange-100 text-sm mb-4">Listening + Reading • 60 Questions</p>
              <div className="flex items-center text-white font-bold text-sm">
                Start Exam <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link to="/test/hsk2/listening" className="group bg-white rounded-3xl p-6 border border-indigo-50 shadow-sm hover:shadow-xl hover:shadow-indigo-500/10 transition-all hover:-translate-y-1">
              <div className="flex justify-between items-start mb-4">
                <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center text-[#4F46E5] group-hover:scale-110 transition-transform">
                  <BookOpen className="w-7 h-7" />
                </div>
                <div className="bg-indigo-50 text-[#4F46E5] px-3 py-1 rounded-full text-xs font-bold">Part 1</div>
              </div>
              <h3 className="text-xl font-bold text-[#312E81] mb-2">Listening Test</h3>
              <p className="text-gray-500 text-sm mb-4">35 题 • 可自定义题量</p>
              <div className="flex items-center text-[#4F46E5] font-bold text-sm">
                Start Practice <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link to="/test/hsk2/reading" className="group bg-white rounded-3xl p-6 border border-emerald-50 shadow-sm hover:shadow-xl hover:shadow-emerald-500/10 transition-all hover:-translate-y-1">
              <div className="flex justify-between items-start mb-4">
                <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center text-emerald-600 group-hover:scale-110 transition-transform">
                  <FileText className="w-7 h-7" />
                </div>
                <div className="bg-emerald-50 text-emerald-600 px-3 py-1 rounded-full text-xs font-bold">Part 2</div>
              </div>
              <h3 className="text-xl font-bold text-[#312E81] mb-2">Reading Test</h3>
              <p className="text-gray-500 text-sm mb-4">25 题 • 可自定义题量</p>
              <div className="flex items-center text-emerald-600 font-bold text-sm">
                Start Practice <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>

          {renderActiveTests()}
        </div>
      ) : (
        <div className="bg-white rounded-3xl p-8 text-center border border-gray-100 shadow-sm mt-8">
          <div className="w-16 h-16 bg-gray-100 text-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Level Locked</h3>
          <p className="text-gray-500">
            HSK {selectedVersion} Level {selectedLevel} is not yet available.
          </p>
        </div>
      )}

      {showRandomTestModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl p-6 w-full max-w-md mx-4 shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#312E81]">Configure Random Test</h3>
              <button
                onClick={() => setShowRandomTestModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400"
              >
                ✕
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">HSK Level 3 Only</label>
                <div className="flex space-x-2">
                  <label className="flex items-center p-3 bg-gray-50 rounded-xl w-full border border-gray-200">
                    <input type="checkbox" checked disabled className="mr-3 w-5 h-5 text-[#4F46E5] rounded focus:ring-[#4F46E5]" />
                    <span className="font-medium text-gray-900">HSK 3</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Test Parts</label>
                <div className="flex space-x-4">
                  {['listening', 'reading'].map((part) => (
                    <label key={part} className="flex-1 flex items-center p-3 bg-gray-50 rounded-xl border border-gray-200 cursor-pointer hover:border-[#4F46E5] transition-colors">
                      <input
                        type="checkbox"
                        checked={randomTestConfig.parts.includes(part)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setRandomTestConfig(prev => ({ ...prev, parts: [...prev.parts, part] }));
                          } else {
                            setRandomTestConfig(prev => ({ ...prev, parts: prev.parts.filter(p => p !== part) }));
                          }
                        }}
                        className="mr-3 w-5 h-5 text-[#4F46E5] rounded focus:ring-[#4F46E5]"
                      />
                      <span className="font-medium text-gray-900 capitalize">{part}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Number of Questions</label>
                <input
                  type="number"
                  min="5"
                  max="50"
                  value={randomTestConfig.numQuestions}
                  onChange={(e) => setRandomTestConfig(prev => ({ ...prev, numQuestions: parseInt(e.target.value) || 20 }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4F46E5] font-medium"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <button onClick={() => setShowRandomTestModal(false)} className="px-6 py-3 text-gray-500 font-bold hover:bg-gray-50 rounded-xl transition-colors">
                Cancel
              </button>
              <button
                onClick={handleRandomTest}
                disabled={randomTestConfig.parts.length === 0}
                className="bg-[#4F46E5] hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-8 py-3 rounded-xl font-bold transition-all shadow-lg shadow-indigo-500/20"
              >
                Generate Test
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardDesktop;
